import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Upload, Download, FileSpreadsheet, AlertTriangle, CheckCircle, Search, Lock } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { formatCurrency, formatDate, formatDateTime } from '../components/subscription/SubscriptionDialogs';
import { UnmatchedCustomers, MatchCustomerDialog, useCustomers } from '../components/subscription/UnmatchedCustomers';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/subscription-management/import`;
const MONTHS = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
const GROUP_STATUS = {
  new: ['Aktarılacak', 'bg-green-100 text-green-700'],
  exists: ['Zaten var', 'bg-gray-100 text-gray-600'],
  blocked: ['Hatalı satır var', 'bg-red-100 text-red-700']
};

const Stat = ({ label, value, color = 'text-gray-800', testId }) => (
  <Card><CardContent className="p-4">
    <p className="text-xs text-gray-500">{label}</p>
    <p className={`text-xl font-bold ${color}`} data-testid={testId}>{value}</p>
  </CardContent></Card>
);

function PreviewResult({ preview, onMatched }) {
  const { summary, groups, errors } = preview;
  const [matchItem, setMatchItem] = useState(null);
  const [locks, setLocks] = useState({});
  const customers = useCustomers();
  const unmatched = preview.unmatched_customers || [];
  const lockCount = Object.keys(locks).length;
  const onLock = (name, lock) => setLocks(l => ({ ...l, [name]: lock }));
  const openSearch = (name) => setMatchItem(unmatched.find(u => u.excel_name === name) || { excel_name: name, row_count: 0, suggestions: [] });
  return (
    <div className="space-y-6">
      <MatchCustomerDialog item={matchItem} customers={customers} onClose={() => setMatchItem(null)} onDone={onLock} />
      {lockCount > 0 && (
        <div className="sticky top-2 z-20 bg-blue-600 text-white rounded-lg px-4 py-3 flex flex-wrap items-center gap-3 shadow-lg" data-testid="import-refresh-banner">
          <Lock className="w-4 h-4" />
          <span className="flex-1 text-sm">{lockCount} müşteri eşleştirildi / eklendi. Aktarılacak dosyaları görmek için önizlemeyi güncelleyin.</span>
          <Button size="sm" variant="secondary" data-testid="import-refresh-btn" onClick={onMatched}>Önizlemeyi Güncelle</Button>
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Stat label="Toplam Satır" value={summary.total_rows} testId="import-total-rows" />
        <Stat label="Hatalı Satır" value={summary.error_rows} color={summary.error_rows ? 'text-red-600' : 'text-gray-800'} testId="import-error-rows" />
        <Stat label="Açılacak Dosya" value={summary.new_cases} color="text-green-600" testId="import-new-cases" />
        <Stat label="Aktarılacak Cihaz" value={summary.new_devices} color="text-blue-600" testId="import-new-devices" />
        <Stat label="Zaten Var / Engellenen" value={`${summary.existing_cases} / ${summary.blocked_cases}`} testId="import-skipped-cases" />
        <Stat label="Beklenen Gelir (1 yıl)" value={formatCurrency(summary.expected_revenue)} testId="import-expected-revenue" />
      </div>
      {summary.term_totals && (
        <div className="flex flex-wrap gap-2 text-sm" data-testid="import-term-totals">
          {['1', '2', '3'].map(t => (
            <span key={t} className="bg-white border rounded-full px-3 py-0.5 text-gray-700">{t} yıl toplam: <b>{formatCurrency(summary.term_totals[t])}</b></span>
          ))}
        </div>
      )}

      {unmatched.length > 0 && <UnmatchedCustomers items={unmatched} locks={locks} customers={customers} onLock={onLock} />}

      {errors.length > 0 && (
        <Card><CardContent className="p-4" data-testid="import-errors">
          <h3 className="font-semibold text-red-700 mb-3 flex items-center gap-2"><AlertTriangle className="w-4 h-4" />Hatalı Satırlar (aktarılmayacak)</h3>
          <div className="max-h-72 overflow-y-auto divide-y text-sm">
            {errors.map(e => (
              <div key={e.row} className="py-2 flex gap-3 items-center" data-testid={`import-error-row-${e.row}`}>
                <span className="text-gray-400 w-16 shrink-0">Satır {e.row}</span>
                <span className="text-gray-700 w-48 md:w-72 shrink-0 truncate" title={e.customer}>{e.customer || '-'} {e.device && `• ${e.device}`}</span>
                <span className="text-red-600 flex-1">{e.message}</span>
                {e.customer && e.message.includes("CRM'de bulunamadı") && (locks[e.customer] ? (
                  <span className="shrink-0 text-xs text-green-700 flex items-center gap-1" data-testid={`import-error-locked-${e.row}`}><Lock className="w-3.5 h-3.5" />{locks[e.customer].type === 'created' ? 'Yeni müşteri eklendi' : 'Eşleştirildi'}</span>
                ) : (
                  <Button size="sm" variant="outline" className="h-7 text-xs shrink-0 flex items-center gap-1" data-testid={`import-error-search-btn-${e.row}`} onClick={() => openSearch(e.customer)}>
                    <Search className="w-3.5 h-3.5" />CRM'de Ara
                  </Button>
                ))}
              </div>
            ))}
          </div>
        </CardContent></Card>
      )}

      <Card><CardContent className="p-4" data-testid="import-groups">
        <h3 className="font-semibold text-gray-700 mb-3">Müşteri Dosyaları</h3>
        <div className="max-h-[480px] overflow-y-auto divide-y text-sm">
          {groups.map(g => {
            const [label, cls] = GROUP_STATUS[g.status];
            return (
              <div key={g.customer_id} className="py-3 flex flex-col md:flex-row md:items-center gap-2" data-testid={`import-group-${g.customer_id}`}>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-800 truncate">{g.customer_name}</p>
                  <p className="text-xs text-gray-500 truncate">
                    {g.device_count} cihaz • Bitiş: {formatDate(g.subscription_end_date)}
                    {g.projects.length > 0 && ` • Proje: ${g.projects.join(', ')}`}
                    {g.contact_phone && ` • ${g.contact_phone}`}
                  </p>
                  {g.out_of_period_count > 0 && <p className="text-xs text-amber-600">{g.out_of_period_count} cihazın bitiş tarihi seçilen ayın dışında</p>}
                  {g.note && <p className="text-xs text-gray-500">{g.note}</p>}
                </div>
                <span className="text-gray-700 md:text-right text-xs md:w-56">
                  {g.term_totals ? ['1', '2', '3'].map(t => `${t}y: ${formatCurrency(g.term_totals[t])}`).join(' • ') : formatCurrency(g.expected_revenue)}
                </span>
                <span className={`px-2 py-0.5 rounded text-xs font-medium w-fit ${cls}`}>{label}</span>
              </div>
            );
          })}
          {groups.length === 0 && <p className="text-gray-500 text-center py-6">Aktarılabilir müşteri bulunamadı</p>}
        </div>
      </CardContent></Card>
    </div>
  );
}

function BatchHistory({ batches }) {
  if (!batches.length) return null;
  return (
    <Card><CardContent className="p-4" data-testid="import-history">
      <h3 className="font-semibold text-gray-700 mb-3">Geçmiş Aktarımlar</h3>
      <div className="divide-y text-sm">
        {batches.map(b => (
          <div key={b.id} className="py-2 flex flex-wrap gap-3 items-center">
            <FileSpreadsheet className="w-4 h-4 text-green-600" />
            <span className="font-medium text-gray-700">{b.file_name}</span>
            <span className="text-gray-500">{b.period}</span>
            <span className="text-gray-500">{b.created_count} dosya</span>
            <span className="text-gray-500">→ {b.representative}</span>
            <span className="text-xs text-gray-400 ml-auto">{formatDateTime(b.committed_at)} • {b.created_by}</span>
          </div>
        ))}
      </div>
    </CardContent></Card>
  );
}

export default function SubscriptionImport({ user }) {
  const token = localStorage.getItem('token');
  const now = new Date();
  const [form, setForm] = useState({ month: String(now.getMonth() + 1), year: String(now.getFullYear()), representative: '', unit_price: '' });
  const [file, setFile] = useState(null);
  const [fileKey, setFileKey] = useState(0);
  const [reps, setReps] = useState([]);
  const [preview, setPreview] = useState(null);
  const [batches, setBatches] = useState([]);
  const [busy, setBusy] = useState(false);
  const isAdmin = user?.role === 'admin';

  const loadBatches = () => axios.get(`${API}/batches?token=${token}`).then(r => setBatches(r.data)).catch(() => {});
  useEffect(() => {
    loadBatches();
    if (isAdmin) {
      axios.get(`${API}/representatives?token=${token}`).then(r => {
        setReps(r.data);
        if (r.data.length === 1) setForm(f => ({ ...f, representative: r.data[0].username }));
      }).catch(() => toast.error('Abonelik temsilcileri yüklenemedi'));
    }
  }, []); // eslint-disable-line

  const downloadTemplate = async () => {
    const res = await axios.get(`${API}/template?token=${token}`, { responseType: 'blob' });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url; a.download = 'abonelik_aktarim_sablonu.xlsx'; a.click();
    URL.revokeObjectURL(url);
  };

  const errorText = (e) => { const d = e.response?.data?.detail; return typeof d === 'string' ? d : 'Hata oluştu'; };

  const runPreview = async () => {
    if (busy) return;
    if (!file) return toast.error('Excel dosyası seçiniz');
    if (isAdmin && !form.representative) return toast.error('Abonelik temsilcisi seçiniz');
    const fd = new FormData();
    fd.append('file', file);
    fd.append('token', token);
    fd.append('period_month', form.month);
    fd.append('period_year', form.year);
    if (form.representative) fd.append('representative', form.representative);
    if (form.unit_price) fd.append('unit_price', form.unit_price);
    setBusy(true);
    setPreview(null);
    try {
      const res = await axios.post(`${API}/preview`, fd);
      setPreview(res.data);
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const commit = async () => {
    if (busy || !preview) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API}/${preview.batch_id}/commit?token=${token}`);
      toast.success(res.data.message);
      setPreview(null);
      setFile(null);
      setFileKey(k => k + 1);
      loadBatches();
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const years = [-1, 0, 1].map(i => String(now.getFullYear() + i));

  return (
    <div className="p-4 md:p-6 bg-gray-50 min-h-screen space-y-6" data-testid="subscription-import-page">
      <div className="flex items-center gap-3">
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-800">Abonelik Excel Aktarımı</h1>
          <p className="text-sm text-gray-500">Her satır bir cihaz; aynı müşterinin cihazları tek dosyada birleşir</p>
        </div>
        <Button data-testid="import-template-btn" variant="outline" size="sm" onClick={downloadTemplate} className="flex items-center gap-2"><Download className="w-4 h-4" />Şablon</Button>
      </div>

      <Card><CardContent className="p-4 grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
        <div>
          <Label>Dönem Ayı</Label>
          <Select value={form.month} onValueChange={(v) => { setForm({ ...form, month: v }); setPreview(null); }}>
            <SelectTrigger data-testid="import-month-select"><SelectValue /></SelectTrigger>
            <SelectContent>{MONTHS.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>Yıl</Label>
          <Select value={form.year} onValueChange={(v) => { setForm({ ...form, year: v }); setPreview(null); }}>
            <SelectTrigger data-testid="import-year-select"><SelectValue /></SelectTrigger>
            <SelectContent>{years.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>Abonelik Temsilcisi</Label>
          {isAdmin ? (
            <Select value={form.representative} onValueChange={(v) => { setForm({ ...form, representative: v }); setPreview(null); }}>
              <SelectTrigger data-testid="import-rep-select"><SelectValue placeholder="Seçiniz" /></SelectTrigger>
              <SelectContent>{reps.map(r => <SelectItem key={r.username} value={r.username}>{r.full_name || r.username}</SelectItem>)}</SelectContent>
            </Select>
          ) : <Input disabled value={user?.full_name || user?.username || ''} />}
        </div>
        <div>
          <Label>1 Yıl Tutarı Boş Satırlar İçin Cihaz Başı Ücret (TL, KDV dahil, ops.)</Label>
          <Input data-testid="import-unit-price-input" type="number" min="0" step="0.01" value={form.unit_price} onChange={(e) => { setForm({ ...form, unit_price: e.target.value }); setPreview(null); }} placeholder="0" />
        </div>
        <div>
          <Label>Excel Dosyası (.xlsx / .xls)</Label>
          <Input key={fileKey} data-testid="import-file-input" type="file" accept=".xlsx,.xlsm,.xls" onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); }} />
        </div>
        <div className="md:col-span-5 flex flex-wrap gap-2 items-center">
          <p className="text-xs text-gray-500 flex-1">Kolonlar: Cihaz No, Abonelik Bitiş Tarihi, Proje, Müşteri, GSM Numarası, 1 Yıl Tutarı, 2 Yıl Tutarı, 3 Yıl Tutarı (cihaz başı, KDV dahil). Beklenen gelir 1 yıllık tutarların toplamıdır. Ünvanı eşleşmeyen müşteriler için CRM'deki müşteriyi seçip eşleştirebilirsiniz.</p>
          <Button data-testid="import-preview-btn" onClick={runPreview} disabled={busy || !file} className="flex items-center gap-2"><Upload className="w-4 h-4" />{busy && !preview ? 'Okunuyor...' : 'Önizle'}</Button>
          {preview && (
            <Button data-testid="import-commit-btn" onClick={commit} disabled={busy || preview.summary.new_cases === 0} className="flex items-center gap-2 bg-green-600 hover:bg-green-700">
              <CheckCircle className="w-4 h-4" />{busy ? 'Aktarılıyor...' : `${preview.summary.new_cases} Dosyayı Aktar`}
            </Button>
          )}
        </div>
      </CardContent></Card>

      {preview && <PreviewResult key={preview.batch_id} preview={preview} onMatched={runPreview} />}
      <BatchHistory batches={batches} />
    </div>
  );
}
